export type InputSource = 'key' | 'pointer' | 'pad';

export interface InputHandlers {
  /** Jump button pressed. `ts` uses the performance.now() clock. */
  down(ts: number, source: InputSource): void;
  up(ts: number): void;
  pause(): void;
  mute(): void;
}

const JUMP_KEYS = new Set(['Space', 'ArrowUp', 'KeyW']);
const PAUSE_KEYS = new Set(['Escape', 'KeyP']);
const PAD_JUMP = [0, 1, 6, 7, 12]; // A, B, triggers, d-pad up
const PAD_START = 9;

/** Elements whose own clicks and keys must not become jumps. */
function isUiTarget(el: EventTarget | null): boolean {
  return el instanceof Element && !!el.closest('button, input, select, textarea, a, label, .panel, [data-ui]');
}

/**
 * One input layer for keyboard, mouse, touch and gamepad. Several sources can
 * hold the button at once; "up" fires only when the last one lets go.
 */
export class Input {
  enabled = false;
  private keys = new Set<string>();
  private pointers = new Set<number>();
  private padHeld = false;
  private padStart = false;

  constructor(surface: HTMLElement, private h: InputHandlers) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    surface.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('blur', () => this.releaseAll(performance.now()));
  }

  private get anyHeld(): boolean {
    return this.keys.size > 0 || this.pointers.size > 0 || this.padHeld;
  }

  releaseAll(ts: number): void {
    const was = this.anyHeld;
    this.keys.clear();
    this.pointers.clear();
    this.padHeld = false;
    if (was) this.h.up(ts);
  }

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === 'KeyM' && !(e.target instanceof HTMLInputElement)) {
      this.h.mute();
      return;
    }
    if (!this.enabled) return;
    if (PAUSE_KEYS.has(e.code)) {
      e.preventDefault();
      this.h.pause();
      return;
    }
    if (!JUMP_KEYS.has(e.code)) return;
    e.preventDefault();
    if (e.repeat || this.keys.has(e.code)) return;
    const was = this.anyHeld;
    this.keys.add(e.code);
    if (!was) this.h.down(e.timeStamp, 'key');
  };

  private onKeyUp = (e: KeyboardEvent) => {
    if (!this.keys.delete(e.code)) return;
    if (!this.anyHeld) this.h.up(e.timeStamp);
  };

  private onPointerDown = (e: PointerEvent) => {
    if (!this.enabled || isUiTarget(e.target)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const was = this.anyHeld;
    this.pointers.add(e.pointerId);
    if (!was) this.h.down(e.timeStamp, 'pointer');
  };

  private onPointerUp = (e: PointerEvent) => {
    if (!this.pointers.delete(e.pointerId)) return;
    if (!this.anyHeld) this.h.up(e.timeStamp);
  };

  /** Gamepads have no events; poll once per frame. */
  pollGamepads(now: number, onMenuPress?: () => void): void {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    let jump = false;
    let start = false;
    let ts = now;
    for (const p of pads) {
      if (!p || !p.connected) continue;
      if (PAD_JUMP.some((i) => p.buttons[i]?.pressed)) {
        jump = true;
        if (p.timestamp && p.timestamp <= now) ts = p.timestamp;
      }
      if (p.buttons[PAD_START]?.pressed) start = true;
    }
    if (start && !this.padStart) {
      if (this.enabled) this.h.pause();
      else onMenuPress?.();
    }
    this.padStart = start;
    if (jump === this.padHeld) return;
    if (jump) {
      if (!this.enabled) {
        this.padHeld = true;
        onMenuPress?.();
        return;
      }
      const was = this.anyHeld;
      this.padHeld = true;
      if (!was) this.h.down(ts, 'pad');
    } else {
      this.padHeld = false;
      if (this.enabled && !this.anyHeld) this.h.up(ts);
    }
  }
}
